/**
 * Daily Policy Counter Utility
 * Tracks policies added between 9am-9pm IST
 * Stored in browser localStorage and synced to Firestore (batched to reduce writes)
 * Resets at 9pm IST when notification is sent
 */

import { db, auth } from '../firebase/firebaseConfig';
import { doc, setDoc, serverTimestamp, increment, runTransaction, getDoc } from 'firebase/firestore';

const STORAGE_KEY = 'policytrackr_daily_counters'; // Isolated from upload counter
const SYNC_INTERVAL = 60 * 60 * 1000; // 1 hour
// Sync threshold: sync only after 5 new policies are pending to reduce writes
const MIN_PENDING_FOR_SYNC = 5;
let syncIntervalId = null;

/**
 * Get current IST date (YYYY-MM-DD format)
 * @returns {string} Today's date in IST
 */
const getISTDate = () => {
  const now = new Date();
  const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
  const istDate = new Date(now.getTime() + istOffset);
  return istDate.toISOString().split('T')[0];
};

/**
 * Load counters from localStorage (shared with upload counter)
 * @returns {Object} Counters object
 */
const loadLocalCounters = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) return JSON.parse(stored);

    // Migration: Check if data exists in the old shared key
    const oldSharedKey = 'policytrackr_upload_counters';
    const oldData = localStorage.getItem(oldSharedKey);
    if (oldData) {
      const parsed = JSON.parse(oldData);
      // Only take daily counter related fields if they exist
      const dailyData = {};
      let hasDailyData = false;

      Object.keys(parsed).forEach(uid => {
        if (parsed[uid].dailyPolicies !== undefined || parsed[uid].pendingDailyPolicies !== undefined) {
          dailyData[uid] = parsed[uid];
          hasDailyData = true;
        }
      });

      if (hasDailyData) {
        console.log('🔄 dailyPolicyCounter: Migrated daily data from shared key');
        saveLocalCounters(dailyData);
        return dailyData;
      }
    }

    return {};
  } catch (e) {
    console.error('Error loading local counters:', e);
    return {};
  }
};

/**
 * Save counters to localStorage (shared with upload counter)
 * @param {Object} counters - Counters to save
 */
const saveLocalCounters = (counters) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(counters));
  } catch (e) {
    console.error('Error saving local counters:', e);
  }
};

/**
 * Sync daily counter to Firestore
 * @param {string} userId - The user's UID
 * @param {Object} counters - Local counters object
 */
const syncDailyCounterToFirestore = async (userId, counters) => {
  if (!userId || !counters[userId]) return false;

  // Ensure user is authenticated and is syncing their own data
  const currentUser = auth.currentUser;
  if (!currentUser || currentUser.uid !== userId) {
    // Silent fail - will try again when this user is authenticated
    return false;
  }

  const counterRef = doc(db, 'upload_counters', userId);

  // Use pending count for atomic increment
  const pendingCount = counters[userId].pendingDailyPolicies || 0;

  if (pendingCount <= 0) {
    return false;
  }

  try {
    // Base update data
    const updateData = {
      dailyPolicies: increment(pendingCount),
      dailyPoliciesDate: getISTDate(),
      lastSyncedDaily: serverTimestamp(),
    };

    // Robust Name Fetching: Fetch agent's name if missing in Firestore or locally "Unknown User"
    try {
      const agentDocRef = doc(db, 'agent-details', userId);
      const agentSnap = await getDoc(agentDocRef);
      if (agentSnap.exists()) {
        const agentData = agentSnap.data();
        updateData.userName = agentData.name || agentData.displayName || 'Unknown User';
        updateData.userEmail = agentData.email || '';
      }
    } catch (nameError) {
      console.warn('⚠️ dailyPolicyCounter: Failed to fetch agent name for sync:', nameError);
    }

    await setDoc(counterRef, updateData, { merge: true });

    // Clear pending count after successful sync
    counters[userId].pendingDailyPolicies = 0;
    counters[userId].pendingDailySync = false;

    saveLocalCounters(counters);
    return true;
  } catch (error) {
    console.error('Error syncing daily counter to Firestore:', error);
    return false;
  }
};

/**
 * Check if we should sync based on the minimum threshold
 * @param {Object} counters - Current local counters
 * @returns {boolean} True if any user has pending daily sync and meets threshold
 */
const shouldSyncDaily = (counters) => {
  for (const userId in counters) {
    if (counters[userId].pendingDailySync && counters[userId].pendingDailyPolicies >= MIN_PENDING_FOR_SYNC) {
      return true;
    }
  }
  return false;
};

/**
 * Sync all users' daily counters
 * @param {boolean} force - If true, syncs even if pending policies < threshold
 */
const syncAllDailyCounters = async (force = false) => {
  const counters = loadLocalCounters();
  let hasChanges = false;

  for (const userId in counters) {
    if (counters[userId].pendingDailySync && (force || counters[userId].pendingDailyPolicies > 0)) {
      const success = await syncDailyCounterToFirestore(userId, counters);
      if (success) hasChanges = true;
    }
  }

  return hasChanges;
};

/**
 * Initialize the daily counter sync system with visibility change handling
 */
export const initDailyCounterSync = () => {
  // Setup visibility change handler
  if (typeof document !== 'undefined') {
    let wasHidden = document.hidden;

    const handleVisibilityChange = () => {
      const isHidden = document.hidden;

      // If the page becomes visible after being hidden, sync if we have any pending daily counts
      if (wasHidden && !isHidden) {
        const counters = loadLocalCounters();
        // Check if we have any pending daily syncs
        const hasPendingDaily = Object.values(counters).some(
          counter => counter.pendingDailySync && counter.pendingDailyPolicies > 0
        );
        if (hasPendingDaily) {
          console.log('🔄 Page became visible with pending daily counts - syncing...');
          syncAllDailyCounters();
        }
      }

      wasHidden = isHidden;
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup function
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }

  return () => { }; // Return empty cleanup function if not in browser
};

/**
 * Start the daily counter sync interval
 */
export const startDailyCounterSyncInterval = () => {
  // Clear any existing interval
  if (syncIntervalId) {
    clearInterval(syncIntervalId);
  }

  // Set up new interval
  syncIntervalId = setInterval(() => {
    syncAllDailyCounters();
  }, SYNC_INTERVAL);

  // Initial sync
  syncAllDailyCounters();

  // Return cleanup function
  return () => {
    if (syncIntervalId) {
      clearInterval(syncIntervalId);
      syncIntervalId = null;
    }
  };
};

/**
 * Initialize daily counter system
 */
export const initDailyCounterSystem = () => {
  const cleanupVisibility = initDailyCounterSync();
  const cleanupInterval = startDailyCounterSyncInterval();

  // Return cleanup function
  return () => {
    cleanupVisibility();
    cleanupInterval();
  };
};

/**
 * Migrate old daily counter to new upload_counters format
 */
const migrateOldDailyCounter = () => {
  try {
    const oldKey = 'policytrackr_daily_policy_counter';
    const oldData = localStorage.getItem(oldKey);

    if (oldData) {
      const oldCounter = JSON.parse(oldData);
      const counters = loadLocalCounters();
      const userId = oldCounter.userId;

      if (userId && oldCounter.count > 0) {
        // Migrate to new format
        if (!counters[userId]) {
          counters[userId] = {};
        }

        counters[userId].dailyPolicies = oldCounter.count;
        counters[userId].dailyPoliciesDate = oldCounter.date;
        // Assume migrated are already synced or let the system handle it
        counters[userId].pendingDailyPolicies = 0;
        counters[userId].pendingDailySync = false;
        counters[userId].lastIncrementDaily = new Date().toISOString();

        saveLocalCounters(counters);
        console.log(`🔄 Migrated old daily counter: ${oldCounter.count} policies for user ${userId}`);

        // Remove old key
        localStorage.removeItem(oldKey);
      }
    }
  } catch (e) {
    console.error('Error migrating old daily counter:', e);
  }
};

/**
 * Increment the daily policy counter
 * @param {string} userId - The user's UID
 * @returns {number} Updated counter value
 */
export const incrementDailyPolicyCounter = (userId) => {
  if (!userId) {
    console.warn('Cannot increment counter: No user ID provided');
    return 0;
  }

  // Migrate old counter on first increment
  migrateOldDailyCounter();

  const counters = loadLocalCounters();
  const today = getISTDate();

  // Initialize user counter if not exists
  if (!counters[userId]) {
    counters[userId] = {
      dailyPolicies: 0,
      dailyPoliciesDate: today,
      pendingDailyPolicies: 0,
      pendingDailySync: false,
      lastIncrementDaily: new Date().toISOString(),
    };
  }

  // Reset if date has changed
  if (counters[userId].dailyPoliciesDate !== today) {
    counters[userId].dailyPolicies = 0;
    counters[userId].pendingDailyPolicies = 0; // Reset pending as well
    counters[userId].dailyPoliciesDate = today;
    counters[userId].pendingDailySync = false;
  }

  // Increment counter
  counters[userId].dailyPolicies = (counters[userId].dailyPolicies || 0) + 1;
  counters[userId].pendingDailyPolicies = (counters[userId].pendingDailyPolicies || 0) + 1;
  counters[userId].lastIncrementDaily = new Date().toISOString();
  counters[userId].pendingDailySync = true; // Mark as pending sync

  saveLocalCounters(counters);

  console.log(`📊 Daily policy counter incremented: ${counters[userId].dailyPolicies} policies added today (pending sync: ${counters[userId].pendingDailyPolicies})`);

  // Sync if pending >= 5 (optimization to reduce writes)
  if (counters[userId].pendingDailyPolicies >= MIN_PENDING_FOR_SYNC) {
    console.log(`📤 Threshold reached (${MIN_PENDING_FOR_SYNC} pending), syncing daily counter...`);
    syncAllDailyCounters();
  }

  return counters[userId].dailyPolicies;
};

/**
 * Get current daily policy count
 * @param {string} userId - The user's UID
 * @returns {number} Current counter value
 */
export const getDailyPolicyCount = (userId) => {
  if (!userId) return 0;

  // Migrate old counter if exists
  migrateOldDailyCounter();

  const counters = loadLocalCounters();
  const today = getISTDate();

  if (!counters[userId]) {
    return 0;
  }

  // Reset if date has changed
  if (counters[userId].dailyPoliciesDate !== today) {
    counters[userId].dailyPolicies = 0;
    counters[userId].pendingDailyPolicies = 0;
    counters[userId].dailyPoliciesDate = today;
    saveLocalCounters(counters);
  }

  return counters[userId].dailyPolicies;
};

/**
 * Reset the daily policy counter (called after 9pm notification)
 * Archives current count to prevDailyPolicies before resetting.
 * @param {string} userId - The user's UID
 */
export const resetDailyPolicyCounter = async (userId) => {
  if (!userId) return;

  // UX/SEO: Force sync any pending policies before resetting, 
  // even if they haven't reached the 5-policy threshold.
  // This ensures the 7pm report is 100% accurate.
  await syncAllDailyCounters(true);

  const counters = loadLocalCounters();

  if (counters[userId]) {
    counters[userId].dailyPolicies = 0;
    counters[userId].pendingDailyPolicies = 0;
    counters[userId].dailyPoliciesDate = getISTDate();
    counters[userId].pendingDailySync = false;
    saveLocalCounters(counters);
  }

  // Sync reset to Firestore with Transaction: Move dailyPolicies -> prevDailyPolicies
  try {
    const counterRef = doc(db, 'upload_counters', userId);

    await runTransaction(db, async (transaction) => {
      const sfDoc = await transaction.get(counterRef);
      if (!sfDoc.exists()) {
        // If doc doesn't exist, just initialize it 
        transaction.set(counterRef, {
          dailyPolicies: 0,
          dailyPoliciesDate: getISTDate(),
          lastSyncedDaily: serverTimestamp(),
          prevDailyPolicies: 0
        });
        return;
      }

      const currentDaily = sfDoc.data().dailyPolicies || 0;

      transaction.update(counterRef, {
        prevDailyPolicies: currentDaily, // Archive current count
        dailyPolicies: 0,                // Reset current count
        dailyPoliciesDate: getISTDate(),
        lastSyncedDaily: serverTimestamp()
      });
    });

    console.log('🔄 Daily policy counter archived and reset to 0 in Firestore');
  } catch (err) {
    console.error('Error resetting daily policy counter in Firestore:', err);
  }
};
